// FieldBlockValue.jsx

import React, { useEffect, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import { getProfilePeople, getProfilePersonName } from 'profile/ProfileFieldValueUtils';

// Services
import { resolveImageUrl } from 'services/storage/StorageAccessService';

// CSS
import './FieldBlockValue.css';

// Components
import Pill from 'components/pill/Pill';

/**
 * FieldBlockValue
 *
 * Renders a field-block value, including cover-image fields that need an actual thumbnail.
 *
 * @param {object} row - The prepared field row.
 * @param {object|null} node - The node providing the field value.
 *
 * @returns {ReactNode} The rendered field value.
 */
const FieldBlockValue = React.memo(({ row, node = null }) => {

	// ----- CONTEXT ----- //

	const { theme } = useTheme();

	// ----- STATE ----- //

	const [resolvedImageUrl, setResolvedImageUrl] = useState('');

	const fieldType = String(row?.fieldType || row?.fieldDef?.type || '').trim().toLowerCase();
	const isCoverField = fieldType === 'cover' || String(row?.fieldKey || '').trim() === 'coverUrl';
	const isTagsField = fieldType === 'tags';
	const isPeopleField = fieldType === 'people';
	const peopleProfiles = isPeopleField
		? (Array.isArray(row?.peopleProfiles) && row.peopleProfiles.length
			? row.peopleProfiles
			: getProfilePeople(row?.value))
		: [];
	const rawValue = typeof row?.value === 'string' ? row.value.trim() : '';
	const tagValues = isTagsField
		? (Array.isArray(row?.value) ? row.value : [row?.value])
			.map((value) => {
				if (value == null) return '';
				if (typeof value === 'object') {
					const candidate = value?.label || value?.name || value?.value;
					return String(candidate || '').trim();
				}
				return String(value).trim();
			})
			.filter(Boolean)
		: [];

	// ----- EFFECTS ----- //

	useEffect(() => {
		let cancelled = false;

		if (!isCoverField || !rawValue) {
			setResolvedImageUrl('');
			return undefined;
		}

		if (rawValue.startsWith('blob:') || rawValue.startsWith('data:')) {
			setResolvedImageUrl(rawValue);
			return undefined;
		}

		// Public pages pre-sign cover URLs server-side. Passing coverStoragePath here
		// would force a client re-sign via the media gate, which fails for anonymous viewers.
		const shouldResolveFromStoragePath = !rawValue.includes('://');

		(async () => {
			try {
				const nextUrl = await resolveImageUrl({
					nodeKey: node?.key || null,
					storagePath: shouldResolveFromStoragePath
						? (node?.coverStoragePath || null)
						: null,
					url: rawValue,
				});

				if (!cancelled) {
					setResolvedImageUrl(typeof nextUrl === 'string' ? nextUrl.trim() : '');
				}
			} catch (_error) {
				if (!cancelled) {
					setResolvedImageUrl('');
				}
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [isCoverField, node?.coverStoragePath, node?.key, rawValue]);

	if (isCoverField) {
		if (resolvedImageUrl) {
			return (
				<div className="field-block-value-container">

					{/* COVER */}
					<img
						className="field-block-value-cover"
						alt={row?.label || node?.title || 'Cover image'}
						src={resolvedImageUrl}
					/>
				</div>
			);
		}

		return (
			<div className="field-block-value-container">

				{/* EMPTY */}
				<div
					className="field-block-value-empty"
				>
					Cover image unavailable.
				</div>
			</div>
		);
	}

	if (isTagsField) {
		if (!tagValues.length) {
			return null;
		}

		return (
			<div className="field-block-value-container">

				{/* PILLS */}
				<div className="field-block-value-pills">
					{tagValues.map((tag, index) => (
						<Pill
							key={`${String(row?.fieldKey || 'tags')}:${tag}:${index}`}
							color={theme.backgroundMuted}
							compact
							label={tag}
							labelColor={theme.foreground}
						/>
					))}
				</div>
			</div>
		);
	}

	if (isPeopleField) {
		if (!peopleProfiles.length) {
			return null;
		}

		return (
			<div className="field-block-value-container">

				{/* PEOPLE */}
				<div className="field-block-value-people-list">
					{peopleProfiles.map((person) => {
						const personKey = String(person?.userKey || person?.key || '').trim();
						const personName = getProfilePersonName(person);

						return (
							<div
								className="field-block-value-people-item"
								key={`${String(row?.fieldKey || 'people')}:${personKey}`}
							>
								{personName}
							</div>
						);
					})}
				</div>
			</div>
		);
	}

	// ----- RENDER ----- //

	return (
		<div className="field-block-value-container">

			{/* TEXT */}
			<div className="field-block-value-text">
				{row?.valueText || ''}
			</div>
		</div>
	);
});

// Dev Tools
FieldBlockValue.displayName = 'FieldBlockValue';

export default FieldBlockValue;
